import { _MiscMessages, ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic, DashboardStatistic_EmptyObject } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { DashboardTotalLicencesLiveButEndedDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-licences-live-but-ended.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-licences',
  templateUrl: './widget-row-licences.component.html',
  styleUrls: ['./widget-row-licences.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowLicencesComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose  : boolean | undefined;
  @Input() containerTitle       : string  | undefined;
  @Input() marginBottom         : string  | undefined="mb-0";
  @Input() showTotalLicenceCosts: boolean | undefined;
  @Input() useContainer         : boolean | undefined;
  @Input() showTitleHintText    : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();
    
  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_Licences, false); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_Licences, 4); }

  private filterCompanyID                   : number | undefined;
  private filterDate                        : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitlePlatformLicencesEndedLive: string="Licences set to end";
  private statTitleTotalCostsLicence        : string="Licence costs";
  private statTitleTotalPlatformLicences    : string="Total licences";
  private statTitleUnusedPlatformLicences   : string="Unallocated licences";
  private statTitleUsedPlatformLicences     : string="Allocated licences";
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number } }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public visibleContainer: boolean=true;

  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private dashboardService   : DashboardService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statTotalPlatformLicences: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-award",
        iconColour    : "info",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "Used or not",
        title         : this.statTitleTotalPlatformLicences,
        value         : undefined,
      }
                   
      let statTotalSims: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-award",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "Device licences",
        title         : this.statTitleUsedPlatformLicences,
        value         : undefined,
      }
                   
      let statUnusedPlatformLicences: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-award",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "Device licences",
        title         : this.statTitleUnusedPlatformLicences,
        value         : undefined,
      }
                   
      let statTotalPlatformLicencesLiveButEnded: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-award",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Allocated",
        title         : this.statTitlePlatformLicencesEndedLive,
        value         : undefined,
      }
      
      let statTotalCostsLicence: DashboardStatistic=DashboardStatistic_EmptyObject();
              
      if (this.showTotalLicenceCosts) {
        statTotalCostsLicence={
          decimalPlaces : 2,
          icon          : "bx-pound",
          iconColour    : "success",
          link          : undefined,
          linkText      : undefined,
          loading       : true,
          percentage    : <string><unknown>"",
          prefix        : "£",
          profit        : "",
          profitUpIsGood: true,
          showProfit    : true,
          showSpinner   : false,
          subTitle      : "Not inc usage",
          title         : this.statTitleTotalCostsLicence,
          value         : undefined,
        }
      }
                
      this.statData.push(statUnusedPlatformLicences);
      this.statData.push(statTotalSims);
      this.statData.push(statTotalPlatformLicencesLiveButEnded);
      this.statData.push(statTotalPlatformLicences);

      if (this.showTotalLicenceCosts)
        this.statData.push(statTotalCostsLicence);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
                    
          const totalCostsLicenceSource=this.showTotalLicenceCosts
                                        ? (
                                           this.pollingWidgets
                                           ? this.dashboardService.getTotalCostsLicenceUpdates(companyId, year, month, day)
                                           : this.dashboardService.getTotalCostsLicence       (companyId, year, month, day)
                                          )
                                        : of(DashboardTotal_Previous_Current_PercentDTO_EmptyObject());
          
          const totalPlatformLicencesSource=this.pollingWidgets
                                            ? this.dashboardService.getTotalPlatformLicencesCountUpdates(companyId, year, month, day)
                                            : this.dashboardService.getTotalPlatformLicencesCount       (companyId, year, month, day);
          
          const totalPlatformLicencesLiveButEndedSource=this.pollingWidgets
                                                        ? this.dashboardService.getTotalPlatformLicencesLiveButEndedUpdates(companyId)
                                                        : this.dashboardService.getTotalPlatformLicencesLiveButEnded       (companyId);
          
          const totalPlatformLicencesUnusedSource=this.pollingWidgets
                                                  ? this.dashboardService.getTotalPlatformLicencesUnusedUpdates(companyId, year, month, day)
                                                  : this.dashboardService.getTotalPlatformLicencesUnused       (companyId, year, month, day);
          
          const totalSimsSource=this.pollingWidgets
                                ? this.dashboardService.getTotalSimCountUpdates(companyId, year, month, day)
                                : this.dashboardService.getTotalSimCount       (companyId, year, month, day);

          return combineLatest({
            totalCostsLicence                : safeStart(totalCostsLicenceSource,                 DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalPlatformLicences            : safeStart(totalPlatformLicencesSource,             DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalPlatformLicencesLiveButEnded: safeStart(totalPlatformLicencesLiveButEndedSource, DashboardTotalLicencesLiveButEndedDTO_EmptyObject()),
            totalPlatformLicencesUnused      : safeStart(totalPlatformLicencesUnusedSource,       DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalSims                        : safeStart(totalSimsSource,                         DashboardTotal_Previous_Current_PercentDTO_EmptyObject())
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20251107 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (this.showTotalLicenceCosts)
          this.applyTotalCostsLicence(result.totalCostsLicence);

        this.applyTotalPlatformLicencesCount       (result.totalPlatformLicences);
        this.applyTotalPlatformLicencesLiveButEnded(result.totalPlatformLicencesLiveButEnded);
        this.applyTotalPlatformLicencesUnused      (result.totalPlatformLicencesUnused);
        this.applyTotalPlatformLicencesUsedCount   (result.totalSims);

        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsLicence(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.showTotalLicenceCosts)
        return;
      
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsLicence) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalPlatformLicencesCount(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalPlatformLicences) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalPlatformLicencesLiveButEnded(data: { liveButEndDated: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitlePlatformLicencesEndedLive) {
          stat.loading   =false,
          stat.percentage=undefined,
          stat.profit    =undefined,
          stat.value     =data.liveButEndDated;

          stat.iconColour=(Number(data.liveButEndDated) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalPlatformLicencesUnused(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleUnusedPlatformLicences) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalPlatformLicencesUsedCount(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleUsedPlatformLicences) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
